import React, { useState, useMemo } from 'react';
import { Button, Alert } from 'antd';
import { createUseStyles } from 'react-jss';
import { useTranslation } from 'react-i18next';
import CustomInput from 'src/components/common/customInput';
import CustomRadioGroup from 'src/components/common/customRadio';
import { EnhancedSqlEditor, AvailableField } from './EnhancedSqlEditor';
import { validateCustomMetric } from './validationUtils';
import { CalculationFunction } from '../calculationMetadataTypes';
import { DraftMetric, AddedMetric } from 'src/reducers/customMetrices';
import lodashCamelcase from 'lodash/camelCase';
import config from 'src/config';
import { AiSqlModal } from './AskAI/AiSqlModal';
import { AskAiButton } from './AskAI/AskAiButton';
import useLazyAPI from 'src/components/common/hooks/useLazyAPI';

const { AI_ANALYTICS } = config;

const useStyles = createUseStyles({
    card: {
        background: '#FAF7EE',
        border: '2px dashed #d9d9d9',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '16px',
    },
    buttonGroup: {
        display: 'flex',
        gap: '8px',
        justifyContent: 'flex-end',
        marginTop: '16px',
    },
    sqlLabel: {
        fontSize: '12px',
        color: '#303030',
        marginBottom: '8px',
        display: 'block',
    },
    sqlContainer: {
        marginBottom: '12px',
    },
    alertContainer: {
        marginTop: '12px',
    },
});

interface DraftMetricCardProps {
    data: DraftMetric;
    onChange: (metric: DraftMetric) => void;
    onAdd: (metric: AddedMetric) => void;
    onCancel: () => void;
    availableFields: AvailableField[];
    calculationFunctions: CalculationFunction[];
    existingMetricNames: string[];
    customColumnType: 'global' | 'local';
    selectedReportType: string;
    buttonLabel?: string;
}

const convertPrettyNameToMetric = (string: string): string => {
    return lodashCamelcase(
        string
            .toLowerCase()
            .split('')
            .map((c) => {
                if ((c >= 'a' && c <= 'z') || (c >= '0' && c <= '9') || c === ' ') {
                    return c;
                }
                return String(c.charCodeAt(0));
            })
            .join(''),
    );
};

export const DraftMetricCard: React.FC<DraftMetricCardProps> = ({
    data,
    onChange,
    onAdd,
    onCancel,
    availableFields,
    calculationFunctions,
    existingMetricNames,
    customColumnType,
    selectedReportType,
    buttonLabel,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [validationError, setValidationError] = useState<string | null>(null);
    
    const [showAiInput, setShowAiInput] = useState(false);
    const [aiPrompt, setAiPrompt] = useState('');
    const [aiResponse, setAiResponse] = useState<any>(null);

    const [askAI, { loading: isLoadingAI }] = useLazyAPI(AI_ANALYTICS.URL, {
        type: 'POST',
        onCompleted: (result) => {
            setAiResponse(result);
        },
        onError: (error) => {
            console.error('Error asking AI:', error);
        },
        baseURL: AI_ANALYTICS.BASE_URL,
    });

    const typeOptions = useMemo(() => [
        { value: 'number', prettyName: t('Number') },
        { value: 'text', prettyName: t('Text') },
        { value: 'boolean', prettyName: t('Boolean') },
        { value: 'timestampz', prettyName: t('Timestamp') },
        { value: 'date', prettyName: t('Date') },
    ], [t]);

    const handleAdd = () => {
        setValidationError(null);

        // Validate
        const validation = validateCustomMetric(
            data.sql,
            data.prettyName,
            availableFields,
            calculationFunctions,
            existingMetricNames,
        );

        if (!validation.valid) {
            setValidationError(validation.error || t('Validation failed'));
            return;
        }

        const metricName = data.name || convertPrettyNameToMetric(data.prettyName);
        const addedMetric: AddedMetric = {
            ...data,
            name: metricName,
            isExpanded: false,
        };

        onAdd(addedMetric);
    };

    const handlePrettyNameChange = (value: string) => {
        onChange({ ...data, prettyName: value });
    };

    const handleTypeChange = (value: string) => {
        onChange({ ...data, type: value });
    };

    const handleSqlChange = (value: string) => {
        onChange({ ...data, sql: value });
    };

    const handleAskAI = async () => {
        askAI({
            variables: {
                query: `${aiPrompt}\n${data.sql ? `Current SQL: ${data.sql}` : ''}`,
                type: AI_ANALYTICS.TYPE.replace('{{selectedReportType}}', selectedReportType)
                    .replace('{{customColumnType}}', customColumnType),
            },
        });
    };

    const handleAcceptAI = () => {
        if (!aiResponse) return;
        onChange({
            ...data,
            prettyName: data.prettyName || aiResponse.column_name,
            type: aiResponse.format,
            sql: aiResponse.sql,
        });

        setShowAiInput(false);
        setAiPrompt('');
        setAiResponse(null);
    };

    const handleRejectAI = () => {
        setAiResponse(null);
    };

    const isFormComplete = data.prettyName && data.type && data.sql;

    return (
        <div className={classes.card}>
            <AskAiButton onClick={() => setShowAiInput(true)} />

            <AiSqlModal
                isOpen={showAiInput}
                onClose={() => setShowAiInput(false)}
                onAccept={handleAcceptAI}
                aiPrompt={aiPrompt}
                setAiPrompt={setAiPrompt}
                aiResponse={aiResponse}
                isLoading={isLoadingAI}
                onAskAi={handleAskAI}
                onReject={handleRejectAI}
                showColumnName={data.prettyName ? false : true}
            />

            <CustomInput
                onChange={handlePrettyNameChange}
                prettyName={t('Pretty Name')}
                value={data.prettyName}
                placeholder={t('Column Name')}
            />

            <CustomRadioGroup
                onChange={handleTypeChange}
                prettyName={t('Column Type')}
                value={data.type}
                data={{ options: typeOptions }}
                direction="horizontal"
            />

            <div className={classes.sqlContainer}>
                <span className={classes.sqlLabel}>{t('SQL Expression')}</span>
                <EnhancedSqlEditor
                    value={data.sql}
                    onChange={handleSqlChange}
                    availableFields={availableFields}
                    calculationFunctions={calculationFunctions}
                    placeholder={t('Enter SQL expression using [[Field Name]] and BI_* functions')}
                    height="180px"
                />
            </div>

            {validationError && (
                <div className={classes.alertContainer}>
                    <Alert
                        type="error"
                        message={t('Validation Error')}
                        description={validationError}
                        showIcon
                        closable
                        onClose={() => setValidationError(null)}
                    />
                </div>
            )}

            <div className={classes.buttonGroup}>
                <Button onClick={onCancel}>{t('Cancel')}</Button>
                <Button type="primary" onClick={handleAdd} disabled={!isFormComplete}>
                    {buttonLabel || t('Add Metric')}
                </Button>
            </div>
        </div>
    );
};

export default DraftMetricCard;
